import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useShallow } from "zustand/shallow"
import { useLiveQueryImmediate } from "@/db/hooks"
import { cn } from "@/shared/utils/general/cn"
import { hasVisibleTitle, getTitleWithFallback } from "@/shared/utils/general/titleUtils"
import type { DbObject } from "@/db/schema"
import type { DbStartAtIndex } from "@/db/schema"
import {
  db,
  ObjectStatus,
  ObjectType,
  StartAtType,
} from "@/db/schema"
import {
  addTask,
  checklistParentPath,
  queryChecklistItems,
} from "@/db/operations"
import { checklistProgress } from "@totodo/operations"
import { useEditFocusStore } from "@/features/task-detail/stores/editFocusStore"
import { SwipeableTaskRow, type SwipeAction } from "./SwipeableTaskRow"
import { TaskDetail } from "@/features/task-detail/TaskDetail"
import { useBreakpoint } from "@/shared/hooks/useBreakpoint"
import { isTouchPointer, useTouchPointer } from "@/shared/utils/platform/pointerKind"
import {
  enhancedSmoothScroll,
  isProperlyInView,
} from "@/shared/utils/ui/enhancedSmoothScroll"
import { useKeyboardStore } from "@/features/auth/stores/keyboardStore"
import { getBottomInset } from "@/shared/utils/ui/bottomInset"
import { useViewContext } from "@/app/stores/viewContext"
import { getViewConfig } from "@/features/views/viewConfig"
import type { ViewId } from "@/features/views/viewRegistry"
import { useSettingBoolean } from "@/features/settings/hooks/useSetting"
import { TaskCheckbox } from "./TaskCheckbox"
import { DateChip } from "./DateChip"
import { ContainerLabel } from "./ContainerLabel"
import { RecurrenceLabel } from "./RecurrenceLabel"
import { TaskFlag } from "./TaskFlag"
import { TaskCounter } from "./TaskCounter"
import { TaskIcons } from "./TaskIcons"
import { PlanIndicator } from "./PlanIndicator"
import { formatDuration } from "@/db/operations"
import { TaskTags } from "./TaskTags"
import { SectionRow } from "./SectionRow"
import { SectionSwipeDialog, type SectionSwipeDialogMode } from "./SectionSwipeDialog"
import { archiveSection, deleteSection } from "@/features/project-header/projectOps"
import { TaskRowAvatars } from "./TaskRowAvatars"
import { UserStatusDot } from "@/shared/components/UserStatusDot"
import { usePendingCompletions } from "@/features/tasks/stores/pendingCompletions"
import { setProjectStatus } from "@/features/project-header/projectStatusRequest"
import { DebugIndexBadge } from "./DebugIndexBadge"
import { TaskContextMenu } from "./TaskContextMenu"
import { MultiSelectContextMenu } from "./MultiSelectContextMenu"
import { useClipboardStore } from "@/shared/stores/clipboardStore"
import { DynamicIcon } from "@/features/navigation/DynamicIcon"
import { ROUTES } from "@/config/routes"
import TimerIcon from "@/shared/icons/pikaicons/stroke/time/timer-default.svg?react"
import { RecurrenceSheet } from "@/features/recurrence"

// ---------------------------------------------------------------------------
// LiveTimerChip — shows a red recording dot + live elapsed time
// ---------------------------------------------------------------------------

function LiveTimerChip({ timerStartedAt, timeSpentMs, className }: {
  timerStartedAt: string | number
  timeSpentMs?: number | null
  className?: string
}) {
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    const startMs = Number(timerStartedAt)
    const update = () => setElapsed(Date.now() - startMs)
    update()
    const id = setInterval(update, 1000)
    return () => clearInterval(id)
  }, [timerStartedAt])

  const total = (timeSpentMs ?? 0) + elapsed
  return (
    <span className={className}>
      <span className="inline-block size-1.5 rounded-full bg-red-500 animate-pulse mr-0.5 align-middle" />
      {" "}{formatDuration(total)}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TaskItemProps {
  task: DbObject
  onComplete: (id: string) => void
  onUncomplete: (id: string) => void
  onSwipeAction?: (id: string, action: SwipeAction) => void
  /** Disable swipe gestures (e.g., during DnD drag) */
  disabled?: boolean
  /** Whether this task's detail panel is expanded */
  isActive?: boolean
  /** Whether this task was just closed (external signal for remount scenarios) */
  isClosing?: boolean
  /** startAtIndex data for date chip display in detail */
  startAtData?: DbStartAtIndex
  /** Whether to auto-focus title on open */
  editTitle?: boolean
  /** Open this task's detail panel */
  onOpen?: (id: string) => void
  /** Close task detail panel */
  onClose?: () => void
  /** Ref callback for the drag handle element (from useSortable.setActivatorNodeRef) */
  dragHandleRef?: (node: HTMLElement | null) => void
  /** Event listeners for the drag handle (from useSortable.listeners) */
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  dragListeners?: Record<string, Function>
  /** Render as drag overlay — compact, no interactions, no swipe */
  isDragOverlay?: boolean
  /** First visible item in a contiguous visual group (rounded top corners) */
  isFirstInGroup?: boolean
  /** Last visible item in a contiguous visual group (rounded bottom corners) */
  isLastInGroup?: boolean
  /** Force-hide the container label (e.g. when already shown in a group header) */
  hideContainerLabel?: boolean
  /** Force-show the date chip even when the current view hides it */
  showDateChip?: boolean
  /** Compact mode — icon+badge for tags, icon-only date, translucent time chip */
  compact?: boolean
  /** CSS zoom applied to the visual row (context menu stays unzoomed) */
  zoom?: number
  /** Single click opens task (bypass selection, for chat/search results) */
  singleClickOpen?: boolean
  /** Show a "new" dot indicator (task created by someone else since last visit) */
  isNew?: boolean
  /** Hide the focus indicator icon (used inside FocusBanner where it's redundant) */
  hideFocusIcon?: boolean
  /** Force a specific viewId for viewConfig lookup (used in FocusBanner for consistent rendering) */
  forceViewId?: ViewId
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Small badge that shows the selection count during multi-drag.
 * Reads count via subscription inside its own component so only THIS
 * component re-renders when the numeric count changes — not every TaskItem.
 */
function DragCounterBadge({ className }: { className?: string }) {
  const count = useViewContext((s) => s.selectedTaskIds.length)
  if (count <= 1) return null
  return (
    <span
      className={cn(
        "drag-counter-avatar absolute -right-1 -top-2.5 z-10 flex size-5 items-center justify-center rounded-full bg-primary text-[11px] font-semibold text-primary-foreground opacity-0 transition-opacity",
        className,
      )}
    >
      {count}
    </span>
  )
}

/**
 * Returns the 1-based selection index for a task.
 * Isolated hook so only selected items re-render when selection order changes.
 */
function useSelectionIndex(taskId: string): number {
  return useViewContext((s) => {
    const idx = s.selectedTaskIds.indexOf(taskId)
    return idx >= 0 ? idx + 1 : 0
  })
}

/**
 * SelectionIcon — isolated component that subscribes to selection index
 * so the parent TaskItem doesn't re-render when the numeric index changes.
 */
function SelectionIcon({
  taskId,
  isSelected,
}: {
  taskId: string
  isSelected: boolean
}) {
  const selectionIndex = useSelectionIndex(taskId)
  return (
    <DynamicIcon
      type="project"
      percentage={isSelected ? 100 : 0}
      unfinished={0}
      status={ObjectStatus.Unfinished}
      size="small"
      color={isSelected ? "var(--primary)" : "currentColor"}
      animate={false}
      label={isSelected ? selectionIndex : undefined}
    />
  )
}

// ---------------------------------------------------------------------------
// SectionBlock — extracted section rendering with swipe support
// ---------------------------------------------------------------------------

interface SectionBlockProps {
  task: DbObject
  taskId: string | undefined
  viewId: string
  isMobile: boolean
  isSelected: boolean
  isSelectionMode: boolean
  disabled?: boolean
  isFirstInGroup?: boolean
  dragHandleRef?: (node: HTMLElement | null) => void
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  dragListeners?: Record<string, Function>
  onOpen?: (id: string, editTitle?: boolean) => void
}

function SectionBlock({
  task,
  taskId,
  viewId,
  isMobile,
  isSelected,
  isSelectionMode,
  disabled,
  isFirstInGroup,
  dragHandleRef,
  dragListeners,
  onOpen,
}: SectionBlockProps) {
  const selectSectionBlock = useViewContext((s) => s.selectSectionBlock)
  const toggleSelectTask = useViewContext((s) => s.toggleSelectTask)
  const rangeSelectTo = useViewContext((s) => s.rangeSelectTo)

  const isGroupedView =
    viewId === "anyday" || viewId === "today" || viewId === "someday"

  // Compute positional child IDs from visibleTaskIds
  // useShallow ensures array reference stability when contents are identical
  const childIds = useViewContext(
    useShallow(
      useCallback(
        (s: { visibleTaskIds: string[]; visibleSectionIds: Set<string> }) => {
          if (!taskId) return []
          const ids = s.visibleTaskIds
          const idx = ids.indexOf(taskId)
          if (idx === -1) return []
          const result: string[] = []
          for (let i = idx + 1; i < ids.length; i++) {
            const id = ids[i]!
            if (id.startsWith("__group_sep__") || s.visibleSectionIds.has(id)) break
            result.push(id)
          }
          return result
        },
        [taskId],
      ),
    ),
  )

  // Dialog state for complete/delete prompts
  const [dialogMode, setDialogMode] = useState<SectionSwipeDialogMode | null>(null)

  const clearSelection = useViewContext((s) => s.clearSelection)

  // Defer single-click so double-click can cancel it
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleSectionClick = useCallback(
    (e: React.MouseEvent) => {
      if (!taskId) return

      // If a section just exited edit mode, absorb this click
      const blurAt = (document as any).__sectionBlurAt as number | undefined
      if (blurAt && Date.now() - blurAt < 300) {
        delete (document as any).__sectionBlurAt
        return
      }

      // Modifier clicks fire immediately (desktop only)
      if (!isMobile && e.shiftKey) {
        e.preventDefault()
        rangeSelectTo(taskId)
        return
      }
      if (!isMobile && (e.ctrlKey || e.metaKey)) {
        e.preventDefault()
        toggleSelectTask(taskId)
        return
      }
      // Cancel any previous pending click before starting a new timer
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current)
      }
      // Defer default click to allow double-click to cancel
      clickTimerRef.current = setTimeout(() => {
        clickTimerRef.current = null
        selectSectionBlock(taskId)
      }, 300)
    },
    [taskId, isMobile, selectSectionBlock, rangeSelectTo, toggleSelectTask],
  )

  const handleSectionDblClick = useCallback(() => {
    // Cancel the pending single-click selection
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current)
      clickTimerRef.current = null
    }
    // Clear any existing selection
    clearSelection()
  }, [clearSelection])

  const handleSectionSwipe = useCallback(
    (action: SwipeAction) => {
      if (!taskId) return

      if (action === "more" || action === "contextMenu") {
        // R→L swipe (short or long): select section + children
        selectSectionBlock(taskId)
      } else if (action === "complete") {
        // L→R: complete section (prompt if has children)
        if (childIds.length > 0) {
          setDialogMode("complete")
        } else {
          archiveSection(taskId, false)
        }
      }
    },
    [taskId, childIds.length, selectSectionBlock],
  )

  const handleSectionOnly = useCallback(() => {
    if (!taskId) return
    setDialogMode(null)
    if (dialogMode === "complete") {
      archiveSection(taskId, false)
    } else {
      deleteSection(taskId, false)
    }
  }, [taskId, dialogMode])

  const handleWithChildren = useCallback(() => {
    if (!taskId) return
    setDialogMode(null)
    if (dialogMode === "complete") {
      archiveSection(taskId, true, childIds)
    } else {
      deleteSection(taskId, true, childIds)
    }
  }, [taskId, dialogMode, childIds])

  return (
    <>
      <div
        data-task-row
        className={cn(
          "task-card-scroll-target task-card-section",
          // Top spacing only when NOT first in container — separates from tasks above
          !isFirstInGroup && (isGroupedView ? "mt-[30px]" : "mt-[30px]"),
        )}
        onClick={handleSectionClick}
        onDoubleClick={handleSectionDblClick}
      >
        <SwipeableTaskRow
          onAction={handleSectionSwipe}
          disabled={disabled}
          borderRadiusClass="rounded-none"
          variant="section"
        >
          <SectionRow
            task={task}
            compact={isGroupedView}
            dragHandleRef={dragHandleRef}
            dragListeners={dragListeners}
            isSelected={isSelected}
            isSelectionMode={isSelectionMode}
            onSelect={() => taskId && selectSectionBlock(taskId)}
            childIds={childIds}
            isFirstInGroup={isFirstInGroup}
            onOpen={onOpen}
          >
            {isSelected && isSelectionMode && (
              <DragCounterBadge className="!-top-1 !-right-1" />
            )}
            <DebugIndexBadge id={task.id} index={task.index} />
          </SectionRow>
        </SwipeableTaskRow>
      </div>

      {/* Complete/Delete confirmation dialog */}
      {dialogMode && (
        <SectionSwipeDialog
          open={!!dialogMode}
          onOpenChange={(open) => { if (!open) setDialogMode(null) }}
          mode={dialogMode}
          sectionTitle={task.title || ""}
          childCount={childIds.length}
          onSectionOnly={handleSectionOnly}
          onWithChildren={handleWithChildren}
        />
      )}
    </>
  )
}

export const TaskItem = memo(function TaskItem({
  task,
  onComplete: _onComplete,
  onUncomplete,
  onSwipeAction,
  disabled,
  isActive,
  isClosing: isClosingProp,
  startAtData,
  editTitle,
  onOpen,
  onClose,
  dragHandleRef,
  dragListeners,
  isDragOverlay,
  isFirstInGroup = true,
  isLastInGroup = true,
  hideContainerLabel,
  showDateChip: forceShowDateChip,
  compact,
  zoom,
  singleClickOpen,
  isNew,
  hideFocusIcon,
  forceViewId,
}: TaskItemProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isCompleted = task.status === ObjectStatus.Completed

  const isCanceled = task.status === ObjectStatus.Canceled
  const { isMobile } = useBreakpoint()
  // Touch input in the desktop layout (iPad landscape): selection toggle, same as phone
  const isTouchInput = useTouchPointer()
  const taskId = task.id ?? ""
  const contextViewId = useViewContext((s) => s.viewId)
  const viewId = forceViewId ?? contextViewId
  const viewConfig = getViewConfig(viewId)
  const shouldShowDateChip = forceShowDateChip ?? viewConfig.showDateChip
  const { value: isCheckboxProgress } = useSettingBoolean("#checkbox-progress")

  // Pending completion state (2s delay before DB commit)
  const queueCompletion = usePendingCompletions((s) => s.add)
  // A project / list row (e.g. scheduled into Today) is completed the container way: the question
  // about its open to-dos comes at once, not after the 2 s grace period of a task
  const isContainerRow = task.type === ObjectType.Project || task.type === ObjectType.List
  const pendingAdd = useCallback(
    (id: string, action: "complete" | "cancel") => {
      if (isContainerRow) {
        void setProjectStatus(id, action === "complete" ? ObjectStatus.Completed : ObjectStatus.Canceled)
        return
      }
      queueCompletion(id, action)
    },
    [isContainerRow, queueCompletion],
  )
  // Pending = in the open batch, or in the batch being collapsed and written.
  const isPendingCompletion = usePendingCompletions((s) =>
    s.pending.has(taskId) || s.committing.has(taskId),
  )
  const pendingAction = usePendingCompletions(
    (s) => s.pending.get(taskId)?.action ?? s.committing.get(taskId),
  )
  const isCollapsing = usePendingCompletions((s) => s.collapsing.has(taskId))

  // ── Selection state (v1 pattern) ──────────────────────────────────────
  // Only subscribe to boolean selectors to avoid re-rendering ALL items on
  // every selection change. `selectedCount` is read imperatively when needed.
  const isSelected = useViewContext((s) => s.selectedTaskIds.includes(taskId))
  // Boolean: is selection mode active? (any task selected) — only changes on 0↔1 boundary
  const isSelectionMode = useViewContext((s) => s.selectedTaskIds.length > 0)
  // Grouped selection borders (checklist pattern): no border/radius between consecutive selected items.
  // Section boundaries always create a visual break (rounded corners at section edges).
  const isFirstSelected = useViewContext((s) => {
    if (!s.selectedTaskIds.includes(taskId)) return false
    const idx = s.visibleTaskIds.indexOf(taskId)
    if (idx <= 0) return true
    const prevId = s.visibleTaskIds[idx - 1]!
    // Break at section boundary: if THIS is a section, always start a new group
    if (s.visibleSectionIds.has(taskId)) return true
    // Break if previous is a section (sections are standalone groups)
    if (s.visibleSectionIds.has(prevId)) return true
    // Break if previous is not selected
    if (!s.selectedTaskIds.includes(prevId)) return true
    return false
  })
  const isLastSelected = useViewContext((s) => {
    if (!s.selectedTaskIds.includes(taskId)) return false
    const idx = s.visibleTaskIds.indexOf(taskId)
    if (idx === -1 || idx >= s.visibleTaskIds.length - 1) return true
    const nextId = s.visibleTaskIds[idx + 1]!
    // Break at section boundary: if NEXT is a section, this is last in its group
    if (s.visibleSectionIds.has(nextId)) return true
    // Break if next is not selected
    if (!s.selectedTaskIds.includes(nextId)) return true
    return false
  })
  const setSingleSelection = useViewContext((s) => s.setSingleSelection)
  const toggleSelectTask = useViewContext((s) => s.toggleSelectTask)
  const clearSelection = useViewContext((s) => s.clearSelection)
  const rangeSelectTo = useViewContext((s) => s.rangeSelectTo)

  // ── Cut visual dimming (internal clipboard) ────────────────────────────
  const isCutTask = useClipboardStore(
    (s) => s.operation === "cut" && s.taskIds.includes(taskId),
  )

  // ── Project/List progress data (for TimelineIcon in checkbox) ─────────
  // ── Task checklist progress (for progress ring on checkbox) ────────────
  const [rawProgressData] = useLiveQueryImmediate(
    async () => {
      if (!taskId) return null

      // Project/List: count child tasks
      if (task.type === ObjectType.Project || task.type === ObjectType.List) {
        const parentPath = `root/${taskId}`
        const children = await db.totodo
          .where("[parentPath+isDeleted]")
          .equals([parentPath, 0])
          .filter((t) => t.type === ObjectType.Task)
          .toArray()
        const total = children.length
        const completed = children.filter(
          (t) => t.status === ObjectStatus.Completed,
        ).length
        const unfinished = children.filter(
          (t) => t.status === ObjectStatus.Unfinished,
        ).length
        return {
          percentage: total > 0 ? (completed / total) * 100 : 0,
          unfinished,
          checklistProgress: null as {
            completed: number
            total: number
          } | null,
        }
      }

      // Regular task: count checklist items — the shared rule the task header and
      // get_task_detail report too. `db` is the live binding (guestDb on a share page).
      if (task.type === ObjectType.Task) {
        const rows = await queryChecklistItems(
          checklistParentPath(task.parentPath, taskId),
          db,
        )
        const { completed, total } = checklistProgress(rows)
        if (total === 0) return null
        return {
          percentage: 0,
          unfinished: 0,
          checklistProgress: { completed, total },
        }
      }

      return null
    },
    ["task-progress", taskId, task.type, task.parentPath],
    null,
  )

  // Stabilize progressData: keep the same object reference when values
  // haven't changed, preventing unnecessary child re-renders.
  const stableProgressRef = useRef(rawProgressData)
  const progressData = useMemo(() => {
    const prev = stableProgressRef.current
    const next = rawProgressData
    if (prev === next) return prev
    if (prev === null || next === null) {
      stableProgressRef.current = next
      return next
    }
    if (
      prev.percentage === next.percentage &&
      prev.unfinished === next.unfinished &&
      prev.checklistProgress?.completed === next.checklistProgress?.completed &&
      prev.checklistProgress?.total === next.checklistProgress?.total
    ) {
      return prev // same values — keep stable reference
    }
    stableProgressRef.current = next
    return next
  }, [rawProgressData])

  // ── Section: render as section header with optional drag support ──────
  if (task.type === ObjectType.Section) {
    // Drag overlay: simplified clone — uses same CSS class (task-card-section)
    // so that margin-top/margin-bottom from globals.css match the original
    // element exactly. Without this dnd-kit's measured rect (which includes
    // margins) won't align with the overlay and the section jumps on grab.
    if (isDragOverlay) {
      return (
        <div className={cn(
          "task-card-scroll-target task-card-section",
          !isFirstInGroup && "mt-[30px]",
        )}>
          <div
            className={cn(
              "relative flex items-center px-3 py-2.5",
              "select-none rounded-lg bg-primary/10",
            )}
          >
            <div className="absolute bottom-0 left-3 right-3 h-px bg-foreground/16 opacity-0" />
            <span className={cn(
              "block flex-1 overflow-x-clip font-bold text-[20px] leading-6 text-ellipsis whitespace-nowrap",
              hasVisibleTitle(task.title) ? "text-primary" : "text-primary/50",
            )}>
              {getTitleWithFallback({ title: task.title, t, fallback: "section" })}
            </span>
          </div>
        </div>
      )
    }

    return (
      <SectionBlock
        task={task}
        taskId={taskId}
        viewId={viewId}
        isMobile={isMobile}
        isSelected={isSelected}
        isSelectionMode={isSelectionMode}
        disabled={disabled}
        isFirstInGroup={isFirstInGroup}
        dragHandleRef={dragHandleRef}
        dragListeners={dragListeners}
        onOpen={onOpen}
      />
    )
  }

  // ── Animation state machine (v1 pattern) ──────────────────────────────
  // Track previous isOpen to detect close transitions.
  // shouldAnimate = true for 2.1s after task closes → plays closing animation.
  //
  // Two trigger paths:
  // 1. Normal: prevIsOpenRef detects isActive going true→false (same component instance)
  // 2. Remount: isClosingProp is true on mount (after virtual↔simple list switch in DndTaskList)
  const prevIsOpenRef = useRef(isActive)
  const [shouldAnimate, setShouldAnimate] = useState(!!isClosingProp)

  useEffect(() => {
    if (prevIsOpenRef.current && !isActive) {
      // Was open → now closed: play closing animation
      setShouldAnimate(true)
    }
    prevIsOpenRef.current = isActive
  }, [isActive])

  // Reset shouldAnimate after closing animation completes (2s + 0.1s margin)
  useEffect(() => {
    if (!shouldAnimate) return
    const timer = setTimeout(() => setShouldAnimate(false), 2100)
    return () => clearTimeout(timer)
  }, [shouldAnimate])

  // ── Scroll intelligence (v1 pattern) ──────────────────────────────────
  // On open:  center the task in view, or near top if too tall
  // On close: scroll into view with block:"nearest" (minimal movement)
  const scrollTargetRef = useRef<HTMLDivElement>(null)

  // Reactive, unlike the getState() read inside scrollOpenIntoView: we need to
  // re-run the scroll when the keyboard opens, not just read its state once.

  /**
   * Bring the opened task fully into view.
   *
   * Extracted so it can run more than once. A single pass is not enough: on
   * mobile the title is auto-focused, the keyboard opens, and with
   * KeyboardResize.Native the WebView itself shrinks — measured on an Android
   * device, `main` went from 855px to 519px roughly 800ms after the task
   * opened, i.e. long after the 50ms settle delay below. The card stayed put
   * and 361px of it, including its own action row, ended up below the fold.
   */
  const scrollOpenIntoView = useCallback(() => {
    const el = scrollTargetRef.current
    if (!el) return
    const container = el.closest<HTMLElement>("main.overflow-y-auto")
    if (!container) return

    // What the action panel actually covers right now, rather than a constant:
    // the panel sits differently on iOS than on Android, and the old constant
    // put the card's own action row behind it. See getBottomInset().
    const actionPanelInset = isMobile ? getBottomInset(container) : 0

    // Center the task, or position it against an edge if it's too tall
    const containerHeight = container.clientHeight
    const visibleHeight = containerHeight - actionPanelInset
    const elHeight = el.getBoundingClientRect().height
    const fitsInView = elHeight < visibleHeight * 0.9
    const keyboardOpen = useKeyboardStore.getState().keyboard?.isOpen ?? false

    if (fitsInView) {
      // Task fits — center it
      if (!isProperlyInView(el, container)) {
        enhancedSmoothScroll(el, {
          container,
          duration: 800,
          block: "center",
          bottomInset: actionPanelInset,
        })
      }
    } else if (keyboardOpen) {
      // Too tall AND the user is typing: the bottom of the card is where the
      // work happens (its action row, the field being edited), so put that in
      // view above the panel instead of the title.
      enhancedSmoothScroll(el, {
        container,
        duration: 800,
        block: "end",
        bottomInset: actionPanelInset + 8,
      })
    } else {
      // Too tall to fit and nothing focused — position title near top
      enhancedSmoothScroll(el, {
        container,
        duration: 800,
        block: "start",
        offset: -48, // breathing room from top so title is clearly visible
        // No bottomInset: block "start" ignores it (see the engine table in
        // dreambase/ui/scroll-into-view.md).
      })
    }
  }, [isMobile])

  useEffect(() => {
    const el = scrollTargetRef.current
    if (!el) return

    // Only act on open or close transitions (not every render)
    const isOpening = isActive
    const isClosing = !isActive && shouldAnimate
    if (!isOpening && !isClosing) return

    // 50ms delay to let DOM + animation settle
    const timer = setTimeout(() => {
      const container = el.closest<HTMLElement>("main.overflow-y-auto")
      if (!container) return

      if (isOpening) {
        scrollOpenIntoView()
      } else {
        // Closing: minimal scroll
        if (!isProperlyInView(el, container)) {
          enhancedSmoothScroll(el, {
            container,
            duration: 800,
            block: "nearest",
          })
        }
      }
    }, 50)

    return () => clearTimeout(timer)
    // shouldAnimate triggers scroll-back on close
  }, [isActive, shouldAnimate, scrollOpenIntoView])

  // Re-run the opening scroll when the scroll container actually changes
  // height — that is the moment the keyboard has resized the WebView. The
  // opening pass runs 50ms after opening, long before that, so without this
  // the card ends up below the fold.
  //
  // Watching the container instead of the keyboard event: the event fires
  // several hundred ms before the resize lands, and by a different margin on
  // iOS than on Android, so any fixed delay is wrong on one of them.
  //
  // Only for a real change (>40px) and at most once per settled size, so the
  // card does not jump on every keystroke — each Enter in the checklist
  // briefly reports the keyboard as closed and open again.
  const lastScrolledHeightRef = useRef(0)
  useEffect(() => {
    if (!isActive) {
      lastScrolledHeightRef.current = 0
      return
    }
    const el = scrollTargetRef.current
    const container = el?.closest<HTMLElement>("main.overflow-y-auto")
    if (!container || typeof ResizeObserver === "undefined") return

    lastScrolledHeightRef.current = container.clientHeight
    let settleTimer: ReturnType<typeof setTimeout> | undefined
    const observer = new ResizeObserver(() => {
      const height = container.clientHeight
      if (Math.abs(height - lastScrolledHeightRef.current) < 40) return
      clearTimeout(settleTimer)
      // Wait for the resize to settle before measuring anything.
      settleTimer = setTimeout(() => {
        lastScrolledHeightRef.current = container.clientHeight
        scrollOpenIntoView()
      }, 120)
    })
    observer.observe(container)
    return () => {
      clearTimeout(settleTimer)
      observer.disconnect()
    }
  }, [isActive, scrollOpenIntoView])

  // ── Handlers ──────────────────────────────────────────────────────────
  const handleToggle = useCallback(() => {
    if (!taskId) return
    // Already completed/canceled in DB — uncomplete immediately (no delay for undo)
    if (isCompleted || isCanceled) {
      onUncomplete(taskId)
      return
    }
    // Pending or new: toggle via pending store (adds with delay, or cancels if already pending)
    pendingAdd(taskId, "complete")
  }, [taskId, isCompleted, isCanceled, onUncomplete, pendingAdd])

  // Complete from task detail: close detail first, then use same pending queue
  const handleDetailComplete = useCallback(
    (id: string) => {
      onClose?.()
      pendingAdd(id, "complete")
    },
    [onClose, pendingAdd],
  )

  const handleSwipe = useCallback(
    (action: SwipeAction) => {
      if (!taskId) return
      if (action === "complete") {
        if (isCompleted) onUncomplete(taskId)
        else pendingAdd(taskId, "complete")
      } else if (action === "contextMenu") {
        // R→L full swipe: open context menu at row center-right
        const el = scrollTargetRef.current
        if (el) {
          const rect = el.getBoundingClientRect()
          setContextMenuPos({ x: rect.right - 32, y: rect.top + rect.height / 2 })
        }
      } else if (action === "schedule") {
        // Open the schedule date picker directly
        useViewContext.getState().openSchedulePicker([taskId])
      } else if (action === "more") {
        // R→L short swipe: toggle multi-select for this task
        toggleSelectTask(taskId)
      } else if (onSwipeAction) {
        onSwipeAction(taskId, action)
      }
    },
    [taskId, isCompleted, onUncomplete, pendingAdd, onSwipeAction, onOpen, toggleSelectTask],
  )

  // §18: Click / tap handling.
  // The ENTIRE row div is the click target (not just the title text).
  // Mobile: single tap opens task or navigates for containers.
  // Desktop: single click = select, double-click = open task detail.
  // Checkbox, DateChip etc. use stopPropagation for independent clicks.
  const handleRowClick = useCallback(
    (e: React.MouseEvent) => {
      if (!taskId) return

      // If a section just exited edit mode, absorb this click
      const blurAt = (document as any).__sectionBlurAt as number | undefined
      if (blurAt && Date.now() - blurAt < 300) {
        delete (document as any).__sectionBlurAt
        return
      }

      // Tap behaviour follows the pointer in use, not only the layout
      // (a finger on an iPad in landscape taps like a phone; a mouse keeps double-click).
      if (isMobile || isTouchPointer()) {
        // Mobile: Projects/Lists navigate directly to their page
        if (task.type === ObjectType.Project) { navigate(ROUTES.PROJECT(taskId)); return }
        if (task.type === ObjectType.List) { navigate(ROUTES.LIST(taskId)); return }
        onOpen?.(taskId)
        return
      }

      // Desktop: Projects/Lists always navigate to their page on click
      if (task.type === ObjectType.Project) { navigate(ROUTES.PROJECT(taskId)); return }
      if (task.type === ObjectType.List) { navigate(ROUTES.LIST(taskId)); return }

      // Desktop: selection modes (Shift, Mod, plain)
      if (e.shiftKey) {
        e.preventDefault()
        e.stopPropagation()
        rangeSelectTo(taskId)
        return
      }
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault()
        e.stopPropagation()
        toggleSelectTask(taskId)
        return
      }
      // Plain click (no modifier) → clear all, select this one task (atomic)
      if (singleClickOpen && onOpen) {
        onOpen(taskId)
        return
      }
      setSingleSelection(taskId)
    },
    [taskId, isMobile, task.type, navigate, onOpen, toggleSelectTask, rangeSelectTo, setSingleSelection, singleClickOpen],
  )

  const handleDoubleClick = useCallback(() => {
    if (isMobile || isTouchPointer() || !onOpen || !taskId) return
    // Projects/Lists navigate to their own page
    if (task.type === ObjectType.Project) { navigate(ROUTES.PROJECT(taskId)); return }
    if (task.type === ObjectType.List) { navigate(ROUTES.LIST(taskId)); return }
    // Double-click on desktop → open task detail (§18)
    clearSelection()
    onOpen(taskId)
  }, [isMobile, onOpen, taskId, task.type, navigate, clearSelection])

  // Mobile selection toggle (matches v1 TimelineToToDoIcon selection)
  const handleMobileSelect = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (!taskId) return
      toggleSelectTask(taskId)
    },
    [taskId, toggleSelectTask],
  )

  // ── Recurrence sheet state (opened from context menu) ──────────────────
  const [showRecurrenceSheet, setShowRecurrenceSheet] = useState(false)

  // ── Context menu state (right-click / long-press) ──────────────────────
  const [contextMenuPos, setContextMenuPos] = useState<{
    x: number
    y: number
  } | null>(null)

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (!taskId) return
      e.preventDefault()
      setContextMenuPos({ x: e.clientX, y: e.clientY })
    },
    [taskId],
  )

  const closeContextMenu = useCallback(() => setContextMenuPos(null), [])

  // ── Insert above/below handlers ─────────────────────────────────────
  const requestEditFocus = useEditFocusStore((s) => s.requestEditFocus)

  const handleAddTask = useCallback(async () => {
    if (!taskId || !task.parentPath) return
    const result = await addTask({
      title: "",
      parentPath: task.parentPath,
      insertPosition: { afterId: taskId },
    })
    if (result.ok && onOpen) {
      requestEditFocus(result.data.id)
      onOpen(result.data.id)
    }
  }, [taskId, task.parentPath, onOpen, requestEditFocus])

  const handleAddSection = useCallback(async () => {
    if (!taskId || !task.parentPath) return
    const result = await addTask({
      title: "",
      type: ObjectType.Section,
      parentPath: task.parentPath,
      insertPosition: { afterId: taskId },
    })
    if (result.ok && onOpen) {
      requestEditFocus(result.data.id)
      onOpen(result.data.id)
    }
  }, [taskId, task.parentPath, onOpen, requestEditFocus])

  // ── Render: Expanded TaskDetail ────────────────────────────────────────
  if (isActive && onClose) {
    return (
      <div
        ref={scrollTargetRef}
        id={`scrolltarget-${taskId}`}
        className="task-card-scroll-target task-row-active"
      >
        {/* .task-row-active CSS: position relative, z-50, pointer-events auto.
            Lifts the card above the ::before dimming overlay (z-30).
            SortableTaskRow also sets z-50 for DnD path compat. */}
        <div className="task-card-open bg-card desk:mx-4 max-w-225">
          <TaskDetail
            task={task}
            startAtData={startAtData}
            editTitle={editTitle}
            onComplete={handleDetailComplete}
            onUncomplete={onUncomplete}
            onClose={onClose}
          />
        </div>
      </div>
    )
  }

  // ── Derived state for compact row ─────────────────────────────────────
  const isSomeday = startAtData?.type === StartAtType.Someday
  const isDone = isCompleted || isCanceled || isPendingCompletion

  // Visual status for checkbox — show filled state during pending
  const visualStatus = isPendingCompletion
    ? pendingAction === "cancel"
      ? ObjectStatus.Canceled
      : ObjectStatus.Completed
    : task.status
  const showContainerLabel =
    viewConfig.showContainerLabel &&
    !hideContainerLabel &&
    task.type !== ObjectType.Project &&
    task.type !== ObjectType.List

  // Show type label ("Project"/"List") as subtitle for containers in views
  // that show ContainerLabel — keeps row height consistent with tasks.
  const showTypeLabel =
    viewConfig.showContainerLabel &&
    !hideContainerLabel &&
    (task.type === ObjectType.Project || task.type === ObjectType.List)

  // For logbook/trash: use statusAt or deletedAt as relative date
  const relativeDate =
    viewId === "logbook"
      ? task.statusAt
      : viewId === "trash"
        ? task.deletedAt
        : undefined

  // ── Render: Drag Overlay (exact clone, no interactions) ─────────────────
  // Renders the same markup as the compact row so the overlay is pixel-perfect.
  // No swipe, no handlers — just a visual clone that follows the cursor/finger.
  if (isDragOverlay) {
    return (
      <div className="task-card-scroll-target">
        <div
          data-task-row
          className={cn(
            "group relative flex min-h-11 items-center gap-2 px-3 py-2",
            "select-none rounded-lg bg-primary/10",
          )}
        >
          <TaskCheckbox
            status={task.status}
            isSomeday={isSomeday}
            taskType={task.type}
            icon={task.icon}
            percentage={progressData?.percentage ?? 0}
            unfinished={progressData?.unfinished ?? 0}
            isRecurringTemplate={task.isRecurringTemplate === 1 ? 1 : 0}
            checklistProgress={
              isCheckboxProgress
                ? (progressData?.checklistProgress ?? null)
                : null
            }
            size="medium"
            onToggle={() => {}}
          />

          <UserStatusDot
            userStatus={task.userStatus}
            taskId={task.id as string}
          />

          {shouldShowDateChip && (
            <DateChip
              startAtData={startAtData}
              viewId={forceShowDateChip ? "project" : viewId}
              relativeDate={relativeDate}
              task={task}
            />
          )}

          <div className="min-w-0 flex-1">
            <div className="flex items-center">
              {isNew && <span className="mr-1.5 size-2 shrink-0 rounded-full bg-primary" />}
              {task.id?.startsWith("$ph-") && !task.title ? (
                <span className="inline-block h-4 w-[60%] animate-pulse rounded bg-muted" />
              ) : (
              <span
                className={cn(
                  "truncate text-base font-semibold",
                  isDone && "text-muted-foreground",
                  !hasVisibleTitle(task.title) && "opacity-50",
                )}
              >
                {getTitleWithFallback({ title: task.title, t, objectType: task.type })}
              </span>
              )}
              {viewConfig.showCounter && (
                <TaskCounter task={task} className="ml-1" />
              )}
              {viewConfig.showIcons && <TaskIcons task={task} hasChecklist={!!progressData?.checklistProgress} hideFocusIcon={hideFocusIcon} viewId={viewId} />}
              <PlanIndicator task={task} className="ml-0.5" />
              {task.progressiveTarget != null && (
                <span className="ml-1 shrink-0 rounded bg-purple-100 px-1 py-0.5 text-[10px] font-medium tabular-nums text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                  🎯 {task.progressiveTarget}
                </span>
              )}
              {task.timerStartedAt && (
                <LiveTimerChip
                  timerStartedAt={task.timerStartedAt}
                  timeSpentMs={task.timeSpentMs}
                  className="ml-1 shrink-0 rounded bg-red-100 px-1 py-0.5 text-[10px] font-medium tabular-nums text-red-700 dark:bg-red-900/30 dark:text-red-300"
                />
              )}
              {!task.timerStartedAt && task.timeSpentMs != null && task.timeSpentMs > 0 && (
                <span className="ml-1 shrink-0 rounded bg-sky-100 px-1 py-0.5 text-[10px] font-medium tabular-nums text-sky-700 dark:bg-sky-900/30 dark:text-sky-300">
                  <TimerIcon className="mr-0.5 inline-block size-3 align-[-1px]" /> {formatDuration(task.timeSpentMs)}
                </span>
              )}
              {task.tags?.length > 0 && (
                <TaskTags task={task} className="ml-1" />
              )}
            </div>
            {showContainerLabel && viewId !== "recurring" && <ContainerLabel task={task} />}
            {viewId === "recurring" && (
              <span className="flex items-center gap-1 truncate text-[11px] leading-tight text-foreground/40">
                <ContainerLabel task={task} className="!inline truncate" />
                <RecurrenceLabel task={task} />
              </span>
            )}
            {showTypeLabel && (
              <span className="block truncate text-[11px] leading-tight text-foreground/40">
                {task.type === ObjectType.Project ? t("Project") : t("List")}
              </span>
            )}
          </div>

          {viewConfig.showAvatars && task.realmId && (
            <TaskRowAvatars realmId={task.realmId} className="ml-1" />
          )}
          {viewConfig.showFlag && task.dueAt && <TaskFlag dueAt={task.dueAt} />}
          <DebugIndexBadge id={task.id} index={task.index} />
        </div>
      </div>
    )
  }

  // ── Render: Compact preview row ────────────────────────────────────────
  // Click overlay pattern (5B.3):
  //   - Handlers on title area div only (not entire row)
  //   - Checkbox, date chip, flag remain independently clickable
  //   - Mobile: tap on title → open. Desktop: double-click → open.
  //   - Ctrl/Cmd+Click on row → toggle selection (v1 pattern)

  // Compute border-radius class once — applied to BOTH the outer wrapper
  // (to clip bg-card from SwipeableTaskRow) and the inner data-task-row.
  const borderRadiusClass = isSelected
    ? isFirstSelected && isLastSelected
      ? "rounded-lg"
      : isFirstSelected
        ? "rounded-t-lg"
        : isLastSelected
          ? "rounded-b-lg"
          : "rounded-none"
    : isFirstInGroup && isLastInGroup
      ? "rounded-lg"
      : isFirstInGroup
        ? "rounded-t-lg"
        : isLastInGroup
          ? "rounded-b-lg"
          : "rounded-none"

  return (
    <div
      ref={scrollTargetRef}
      id={`scrolltarget-${taskId}`}
      className={cn(
        "task-card-scroll-target",
        borderRadiusClass,
        shouldAnimate && "task-card-closing",
        // The collapsing set is global, keyed by task id. Only hide the row
        // while it still shows its old status: once the write lands, the same
        // task in Logbook/Trash (or any view that shows it completed) must stay
        // visible. In the view it left, the row is gone by then anyway.
        isCollapsing && !isCompleted && !isCanceled && "task-row-collapsing",
      )}
    >
      <SwipeableTaskRow
        onAction={handleSwipe}
        disabled={disabled}
        borderRadiusClass={borderRadiusClass}
        style={zoom ? { zoom } : undefined}
        muted={hideFocusIcon}
      >
        <div
          data-task-row
          className={cn(
            "group relative flex min-h-11 cursor-pointer items-center gap-2 px-3 py-2",
            "select-none transition-colors duration-150",
            isSelected
              ? "bg-primary/10"
              : !isMobile && "hover:bg-muted/50 hover:rounded-lg",
            isPendingCompletion && "task-pending-completion",
            isCutTask && "opacity-40",
            task.isRecurringTemplate === 1 && viewId !== "recurring" && "opacity-60",
            borderRadiusClass,
          )}
          onClick={handleRowClick}
          onDoubleClick={handleDoubleClick}
          onContextMenu={handleContextMenu}
        >
          {/* Drag counter avatar — shown when multiple selected (v1 pattern).
              Count is read imperatively to avoid subscribing ALL items. */}
          {isSelected && isSelectionMode && <DragCounterBadge />}

          {/* Checkbox — independently clickable (has z-20 + stopPropagation) */}
          <TaskCheckbox
            status={visualStatus}
            isPendingCompletion={isPendingCompletion}
            isSomeday={isSomeday}
            taskType={task.type}
            icon={task.icon}
            percentage={progressData?.percentage ?? 0}
            unfinished={progressData?.unfinished ?? 0}
            isRecurringTemplate={task.isRecurringTemplate === 1 ? 1 : 0}
            checklistProgress={
              isCheckboxProgress
                ? (progressData?.checklistProgress ?? null)
                : null
            }
            size="medium"
            onToggle={handleToggle}
          />

          {/* UserStatus dot — §21: between checkbox and date chip */}
          <UserStatusDot
            userStatus={task.userStatus}
            taskId={task.id as string}
          />

          {/* Date chip — independently clickable */}
          {shouldShowDateChip && (
            <DateChip
              startAtData={startAtData}
              viewId={forceShowDateChip ? "project" : viewId}
              relativeDate={relativeDate}
              task={task}
              compact={compact}
            />
          )}

          {/* Recurring template: "Waiting" chip in the date slot */}
          {task.isRecurringTemplate === 1 && (
            <span className="flex shrink-0 items-center rounded px-1.5 py-1.5 bg-foreground/10 text-foreground/40 text-[11px] font-normal leading-none">
              Waiting
            </span>
          )}

          {/* Title area — also serves as the invisible drag activator when
              dragHandleRef is provided. touch-action:none prevents the compositor
              from claiming the touch so ListDragSensor can decide scroll/swipe/drag.
              On mobile, we MUST NOT set touch-action:none — it kills native scroll.
              Click/double-click is handled by the outer row div — not here. */}
          <div
            ref={
              dragHandleRef
                ? (node: HTMLElement | null) => dragHandleRef(node)
                : undefined
            }
            {...(dragListeners
              ? (dragListeners as React.HTMLAttributes<HTMLDivElement>)
              : {})}
            className="min-w-0 flex-1"
            style={dragHandleRef && !isMobile ? { touchAction: "none" } : undefined}
          >
            {/* Row 1: Title + icons */}
            <div className="flex items-center">
              {isNew && <span className="mr-1.5 size-2 shrink-0 rounded-full bg-primary" />}
              {task.id?.startsWith("$ph-") && !task.title ? (
                <span className="inline-block h-4 w-[60%] animate-pulse rounded bg-muted" />
              ) : (
              <span
                className={cn(
                  "truncate text-base font-semibold",
                  isDone && "text-muted-foreground",
                  !hasVisibleTitle(task.title) && "opacity-50",
                  task.isRecurringTemplate === 1 &&
                    (task.type === ObjectType.Project || task.type === ObjectType.List) &&
                    "font-bold",
                )}
              >
                {getTitleWithFallback({ title: task.title, t, objectType: task.type })}
              </span>
              )}

              {/* Chevron for recurring projects (Things 3 style) */}
              {task.isRecurringTemplate === 1 &&
                (task.type === ObjectType.Project || task.type === ObjectType.List) && (
                <span className="ml-1 shrink-0 text-foreground/30 text-sm">›</span>
              )}

              {/* Counter badge (Project/List only) */}
              {viewConfig.showCounter && (
                <TaskCounter task={task} className="ml-1" />
              )}

              {/* Note/checklist/reminder indicators */}
              {viewConfig.showIcons && <TaskIcons task={task} hasChecklist={!!progressData?.checklistProgress} hideFocusIcon={hideFocusIcon} viewId={viewId} />}

              {/* Plan link indicator */}
              <PlanIndicator task={task} className="ml-0.5" />

              {/* Progressive overload target */}
              {task.progressiveTarget != null && (
                <span className="ml-1 shrink-0 rounded bg-purple-100 px-1 py-0.5 text-[10px] font-medium tabular-nums text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                  🎯 {task.progressiveTarget}
                </span>
              )}



              {/* Time tracking indicator */}
              {task.timerStartedAt && (
                <LiveTimerChip
                  timerStartedAt={task.timerStartedAt}
                  timeSpentMs={task.timeSpentMs}
                  className={cn(
                    "ml-1 shrink-0 rounded px-1 py-0.5 text-[10px] font-medium tabular-nums",
                    compact
                      ? "bg-primary/15 text-primary"
                      : "time-chip-recording"
                  )}
                />
              )}
              {!task.timerStartedAt && task.timeSpentMs != null && task.timeSpentMs > 0 && (
                <span className={cn(
                  "ml-1 shrink-0 rounded px-1 py-0.5 text-[10px] font-medium tabular-nums",
                  compact
                    ? "bg-primary/15 text-primary"
                    : "time-chip"
                )}>
                  <TimerIcon className="mr-0.5 inline-block size-3 align-[-1px]" /> {formatDuration(task.timeSpentMs)}
                </span>
              )}

              {/* Tag chips (desktop) / icon (mobile) */}
              {task.tags?.length > 0 && (
                <TaskTags task={task} className="ml-1" iconOnly={compact} />
              )}
            </div>

            {/* Row 2: Container label (project name) or type label (Project/List) */}
            {showContainerLabel && viewId !== "recurring" && <ContainerLabel task={task} />}
            {viewId === "recurring" && (
              <span className="flex items-center gap-1 truncate text-[11px] leading-tight text-foreground/40">
                <ContainerLabel task={task} className="!inline truncate" />
                <RecurrenceLabel task={task} />
              </span>
            )}
            {showTypeLabel && (
              <span className="block truncate text-[11px] leading-tight text-foreground/40">
                {task.type === ObjectType.Project ? t("Project") : t("List")}
              </span>
            )}
          </div>

          {/* Avatars for shared tasks (v1 pattern) */}
          {viewConfig.showAvatars && task.realmId && (
            <TaskRowAvatars realmId={task.realmId} className="ml-1" />
          )}

          {/* Right side: Flag + deadline — independently clickable */}
          {viewConfig.showFlag && task.dueAt && <TaskFlag dueAt={task.dueAt} compact={compact} />}

          {/* Mobile selection controls — TimelineIcon: 100% filled when selected, 0% empty when not.
              Always mounted during selection mode with CSS transitions for smooth enter/exit. */}
          {(isMobile || isTouchInput) && isSelectionMode && (
            <button
              type="button"
              className="ml-2 flex shrink-0 items-center transition-[opacity,transform] duration-150 ease-out"
              style={{
                opacity: isSelected ? 1 : 0.3,
                transform: "scale(1)",
              }}
              onClick={handleMobileSelect}
              aria-label={isSelected ? t("Deselect task") : t("Select task")}
            >
              <SelectionIcon taskId={taskId} isSelected={isSelected} />
            </button>
          )}
          <DebugIndexBadge id={task.id} index={task.index} />
        </div>
      </SwipeableTaskRow>

      {/* Context menu — rendered outside SwipeableTaskRow to avoid clipping */}
      {contextMenuPos &&
      isSelected &&
      isSelectionMode &&
      useViewContext.getState().selectedTaskIds.length > 1 ? (
        <MultiSelectContextMenu
          taskIds={useViewContext.getState().selectedTaskIds}
          position={contextMenuPos}
          onClose={closeContextMenu}
        />
      ) : contextMenuPos ? (
        <TaskContextMenu
          task={task}
          position={contextMenuPos}
          onClose={closeContextMenu}
          onSchedule={onOpen && taskId ? () => onOpen(taskId) : undefined}
          onMove={taskId ? () => useViewContext.getState().openMovePicker([taskId]) : undefined}
          onTags={onOpen && taskId ? () => onOpen(taskId) : undefined}
          onAddTask={task.parentPath ? handleAddTask : undefined}
          onAddSection={task.parentPath ? handleAddSection : undefined}
          onRepeat={() => setShowRecurrenceSheet(true)}
        />
      ) : null}

      {/* Recurrence sheet — opened from context menu "Repeat" */}
      {showRecurrenceSheet && taskId && (
        <RecurrenceSheet
          open={showRecurrenceSheet}
          onOpenChange={setShowRecurrenceSheet}
          task={task}
        />
      )}
    </div>
  )
})
